import React, { useState, useEffect, useCallback } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import { dashboardApi } from '../services/dashboardApi';
import api from '../services/api';
import { LABELS, getGreeting } from '../constants/dashboard';
import { STOCK_STATUS } from '../constants/vehicleStatus';
import { useAuth } from '../contexts/AuthContext';
import {
  OnboardingChecklist,
  HeroSection,
  NotificationCenter,
  VisibilityPanel,
  StockHealthDrawer,
  StockAnalytics,
} from '../components/dashboard';

const Dashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [visibility, setVisibility] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [drawer, setDrawer] = useState({ open: false, category: null, vehicles: [] });
  const [dismissedRecs, setDismissedRecs] = useState([]);

  const loadData = useCallback(async () => {
    try {
      const [result, visibilityData, insightsData] = await Promise.all([
        dashboardApi.fetchAll(),
        dashboardApi.getVisibility(),
        dashboardApi.getInsights(),
      ]);
      setData(result);
      setVisibility(visibilityData);
      setInsights(insightsData);
    } catch {
      setError(LABELS.loadError);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Init dismissed recommendations from settings
  useEffect(() => {
    if (data?.settings?.dismissed_recommendations) {
      setDismissedRecs(data.settings.dismissed_recommendations);
    }
  }, [data?.settings?.dismissed_recommendations]);

  const handleDismissRec = useCallback(async (recId) => {
    setDismissedRecs(prev => {
      const updated = [...prev, recId];
      api.put('/settings', { dismissed_recommendations: updated }).catch(() => {});
      return updated;
    });
  }, []);

  // Refresh on focus/visibility
  useEffect(() => {
    const onFocus = () => loadData();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') loadData();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [loadData]);

  if (loading) {
    return (
      <div className="py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // --- Compute derived state ---
  const {
    vehicles, publications, settings, appointments, offers,
    listings, oauthAccounts,
  } = data;

  // Upcoming appointments
  const now = new Date();
  const todayStr = now.toISOString().slice(0, 10);
  const nowTimeStr = now.toTimeString().slice(0, 5);
  const isAppointmentPast = (a) => {
    if (a.status === 'completed') return true;
    if (a.status !== 'pending' && a.status !== 'confirmed') return false;
    return a.date < todayStr || (a.date === todayStr && a.time < nowTimeStr);
  };
  const upcomingAppointments = appointments.filter(a =>
    (a.status === 'pending' || a.status === 'confirmed') && !isAppointmentPast(a)
  );

  // Vitrine vehicle count
  const vitrineCount = settings?.vitrine_enabled
    ? vehicles.filter(v => v.stock_status !== STOCK_STATUS.SOLD && v.stock_status !== STOCK_STATUS.IN_PREPARATION && v.images?.length > 0).length
    : 0;

  // Onboarding
  const completedSteps = {
    social: oauthAccounts.length > 0,
    vehicle: vehicles.length > 0,
    marketplace: listings.length > 0,
    vitrine: settings?.vitrine_enabled && settings?.dealer_slug,
  };
  const allOnboardingDone = Object.values(completedSteps).every(Boolean);

  // Greeting
  const firstName = user?.name?.split(' ')[0] || '';
  const pendingOffersCount = offers.filter(o => {
    const status = (o.latest_offer || o).status;
    return status === 'pending' || status === 'countered';
  }).length;
  const pendingAppointmentsCount = upcomingAppointments.filter(a => a.status === 'pending').length;
  const pendingCount = pendingOffersCount + pendingAppointmentsCount;
  const isNewUser = !allOnboardingDone && vehicles.length === 0;
  const { greeting, subtitle: greetingSub } = getGreeting(firstName, 'fr', pendingCount, isNewUser);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-[26px] font-extrabold tracking-tight text-primary">{greeting}</h1>
        <p className="text-sm text-secondary mt-1">
          {pendingCount > 0 && !isNewUser
            ? <>Vous avez <span className="text-amber-400 font-semibold">{pendingCount} action{pendingCount > 1 ? 's' : ''} en attente</span> — on s'en occupe ?</>
            : greetingSub
          }
        </p>
      </div>

      {error && <div className="bg-red-500/10 text-red-400 p-4 rounded-md">{error}</div>}

      {/* Onboarding: Hero for brand new users, checklist for returning users */}
      {!allOnboardingDone && (
        isNewUser
          ? <HeroSection firstName={firstName} completedSteps={completedSteps} />
          : <OnboardingChecklist completedSteps={completedSteps} />
      )}

      {/* Notification Center */}
      <NotificationCenter
        vehicles={vehicles}
        offers={offers}
        upcomingAppointments={upcomingAppointments}
        recommendations={visibility?.recommendations}
        dismissedRecs={dismissedRecs}
        onDismissRec={handleDismissRec}
      />

      {/* Online Visibility — StockAnalytics injected between Row 1 and Diffusion */}
      <VisibilityPanel
        data={visibility}
        vehicles={vehicles}
        settings={settings}
        interest={insights?.interest}
        onCategoryClick={(cat, list) => setDrawer({ open: true, category: cat, vehicles: list })}
      >
        <StockAnalytics vehicles={vehicles} sellDuration={insights?.sell_duration} />
      </VisibilityPanel>

      <StockHealthDrawer
        open={drawer.open}
        category={drawer.category}
        vehicles={drawer.vehicles}
        onClose={() => setDrawer({ open: false, category: null, vehicles: [] })}
      />
    </div>
  );
};

export default Dashboard;
